import type { ReactNode } from "react";
import { ArrowRight, BedDouble, CalendarDays, CreditCard, LayoutGrid, PawPrint, ShieldCheck, Wallet } from "lucide-react";

import { useAnimalPhotoUrls } from "../../api/useAnimalPhotoUrls";
import { formatEuro } from "../../i18n/format";
import { geslachtTeken, type Context } from "../pensionklant/hulp";
import type { Berekening } from "./aanvraag";
import { langeDatum, useKlantDieren, type GekozenKlant, type Periode, type Vertaal } from "./bouwstenen";

function Blok({ icoon, titel, children }: { icoon: ReactNode; titel: string; children: ReactNode }) {
  return (
    <section className="nr-som__blok" aria-label={titel}>
      <span className="nr-rond" aria-hidden="true">
        {icoon}
      </span>
      <div className="nr-som__inhoud">
        <strong>{titel}</strong>
        {children}
      </div>
    </section>
  );
}

/**
 * De samenvatting rechts op de laatste twee stappen: periode, dieren, verblijven, arrangementen, verzekering en het
 * totaal als indicatie, met de aanbetaling die na goedkeuring volgt. Met {@code uitgebreid} ook de opbouw van het
 * totaal.
 */
export function Samenvatting({
  klant,
  dierIds,
  periode: p,
  berekening: b,
  laden,
  ctx,
  uitgebreid,
}: {
  klant: GekozenKlant;
  dierIds: number[];
  periode: Periode;
  berekening: Berekening | undefined;
  laden: boolean;
  ctx: Context;
  uitgebreid?: boolean;
}) {
  const t = ctx.t as Vertaal;
  const { locale } = ctx;
  const { dieren } = useKlantDieren(klant.id);
  const gekozen = dieren.filter((d) => dierIds.includes(d.id));
  const fotos = useAnimalPhotoUrls(gekozen.map((d) => d.id));
  const eur = (n: number) => formatEuro(n, locale);
  const extrasPer = (dierId: number | null) => (b?.extras ?? []).filter((e) => e.dierId === dierId);

  return (
    <aside className="nr-som" aria-label={t("nieuweReservering.som.titel")}>
      <h4>{t("nieuweReservering.som.titel")}</h4>
      <Blok icoon={<CalendarDays size={20} />} titel={t("nieuweReservering.som.periode")}>
        <span className="nr-regel">
          {langeDatum(p.aankomst, locale)} {p.aankomstTijd}
          <ArrowRight size={14} aria-hidden="true" />
          {langeDatum(p.vertrek, locale)} {p.vertrekTijd}
        </span>
        {b && (
          <span className="nr-klein">
            {b.nachten === 1 ? t("nieuweReservering.periode.nacht") : t("nieuweReservering.periode.nachten", { args: [b.nachten] })}
          </span>
        )}
      </Blok>
      <Blok icoon={<PawPrint size={20} />} titel={t("nieuweReservering.som.dieren")}>
        {gekozen.map((d) => (
          <span key={d.id} className="nr-som__dier">
            {fotos[String(d.id)] ? (
              <img src={fotos[String(d.id)]} alt="" />
            ) : (
              <span className="nr-dier__foto--leeg" aria-hidden="true">
                <PawPrint size={16} />
              </span>
            )}
            <span>
              <strong>
                {d.naam} {geslachtTeken(d.geslacht)}
              </strong>
              <span className="nr-klein">{[d.diersoort, d.ras].filter(Boolean).join(" · ")}</span>
            </span>
          </span>
        ))}
      </Blok>
      <Blok icoon={<BedDouble size={20} />} titel={t("nieuweReservering.som.verblijven")}>
        {(b?.dieren ?? []).map((d) => (
          <span key={d.dierId} className="nr-som__rij">
            <strong>{d.naam}</strong>
            <span>{d.accommodatie ?? t("nieuweReservering.som.geenVerblijf")}</span>
          </span>
        ))}
      </Blok>
      {b && b.extras.length > 0 && (
        <Blok icoon={<LayoutGrid size={20} />} titel={t("nieuweReservering.som.extras")}>
          {[...b.dieren.map((d) => ({ id: d.dierId as number | null, naam: d.naam })), { id: null, naam: t("nieuweReservering.arrangementen.reservering") }]
            .filter((s) => extrasPer(s.id).length > 0)
            .map((s) => (
              <div key={s.id ?? "reservering"} className="nr-som__groep">
                <strong>{s.naam}</strong>
                {extrasPer(s.id).map((e) => (
                  <span key={`${s.id}-${e.arrangementId}`} className="nr-som__regel">
                    <span>{e.aantal}×</span>
                    <span className="nr-som__naam">{e.naam}</span>
                    <span>{eur(e.bedrag)}</span>
                  </span>
                ))}
              </div>
            ))}
        </Blok>
      )}
      {b && (b.verzekering > 0 || gekozenProducten(b).length > 0) && (
        <Blok icoon={<ShieldCheck size={20} />} titel={t("nieuweReservering.som.verzekering")}>
          {b.verzekering > 0 && (
            <span className="nr-som__regel">
              <span className="nr-som__naam">{t("nieuweReservering.keuzes.verzekering")}</span>
              <span>{eur(b.verzekering)}</span>
            </span>
          )}
          {gekozenProducten(b).map((p) => (
            <span key={p.id} className="nr-som__regel">
              <span className="nr-som__naam">{p.naam}</span>
              <span>{eur(p.prijs)}</span>
            </span>
          ))}
        </Blok>
      )}
      {uitgebreid && b && (
        <Blok icoon={<Wallet size={20} />} titel={t("nieuweReservering.som.opbouw")}>
          <span className="nr-som__regel">
            <span className="nr-som__naam">{t("nieuweReservering.som.verblijf")}</span>
            <span>{eur(b.verblijf)}</span>
          </span>
          <span className="nr-som__regel">
            <span className="nr-som__naam">{t("nieuweReservering.som.extras")}</span>
            <span>{eur(b.extrasTotaal)}</span>
          </span>
          {b.verzekering > 0 && (
            <span className="nr-som__regel">
              <span className="nr-som__naam">{t("nieuweReservering.keuzes.verzekering")}</span>
              <span>{eur(b.verzekering)}</span>
            </span>
          )}
          {gekozenProducten(b).map((p) => (
            <span key={p.id} className="nr-som__regel">
              <span className="nr-som__naam">{p.naam}</span>
              <span>{eur(p.prijs)}</span>
            </span>
          ))}
        </Blok>
      )}
      <div className="nr-som__totaal" role="status">
        <span>
          <strong>{t("nieuweReservering.som.totaal")}</strong>
          {!uitgebreid && <span className="nr-klein">{t("nieuweReservering.som.totaalUitleg")}</span>}
        </span>
        <strong className="nr-som__bedrag">{b ? eur(b.totaal) : laden ? "…" : "—"}</strong>
      </div>
      {uitgebreid && b && b.aanbetalingPercentage > 0 && (
        <div className="nr-som__aanbetaling">
          <CreditCard size={20} aria-hidden="true" />
          <span>
            <strong>{t("nieuweReservering.som.naGoedkeuring")}</strong>
            <span className="nr-klein">{t("nieuweReservering.som.aanbetaling", { args: [b.aanbetalingPercentage] })}</span>
          </span>
          <strong>{eur(b.aanbetaling)}</strong>
        </div>
      )}
    </aside>
  );
}

/** De andere reserveringsproducten die in deze berekening gekozen zijn. */
function gekozenProducten(b: Berekening) {
  return (Array.isArray(b.producten) ? b.producten : []).filter((p) => p.gekozen);
}
